import { useApiClientBase } from "./apiClient";

/**
 * useDropdownApi is a custom hook that provides API functions
 * for fetching dropdown values for various filters in the application.
 * It uses the configured API client and error handler from useApiClientBase.
 *
 * @returns {Object} - Functions to fetch dropdown values for markets, MCOs, countries, company codes, years, months, scenarios, and period details.
 */
export const useDropdownApi = () => {
  const { apiClient, handleError } = useApiClientBase();

  /**
   * Fetches market dropdown values from the backend API.
   *
   * @param params - (Optional) Query parameters to filter market values.
   * @returns {Promise<any>} - The response data containing market values, or null if an error occurs.
   */
  const getMarketDropdownValues = async (params = {}) => {
    try {
      const response = await apiClient.get("/CascadingFilter/marketvalues", {
        params,
      });
      return response.data;
    } catch (err) {
      return handleError(err, "fetching Market dropdown values failed");
    }
  };

  /**
   * Fetches MCO (Managed Care Organization) dropdown values from the backend API.
   *
   * @param params - (Optional) Query parameters to filter MCO values.
   * @returns {Promise<any>} - The response data containing MCO values, or null if an error occurs.
   */
  const getMcoDropdownValues = async (params = {}) => {
    try {
      const response = await apiClient.get("/CascadingFilter/mcos", { params });
      return response.data;
    } catch (err) {
      return handleError(err, "fetching MCO dropdown values failed");
    }
  };

  /**
   * Fetches country dropdown values from the backend API.
   *
   * @param params - (Optional) Query parameters to filter country values.
   * @returns {Promise<any>} - The response data containing country values, or null if an error occurs.
   */
  const getCountryDropdownValues = async (params = {}) => {
    try {
      const response = await apiClient.get("/CascadingFilter/countries", {
        params,
      });
      return response.data;
    } catch (err) {
      return handleError(err, "fetching country dropdown values failed");
    }
  };

  /**
   * Fetches market values based on country from the backend API.
   *
   * @param params - (Optional) Query parameters to filter market values by country.
   * @returns {Promise<any>} - The response data containing market values, or null if an error occurs.
   */
  const countryBasedMarketValues = async (params = {}) => {
    try {
      const response = await apiClient.get(
        "/CascadingFilter/countrybasedmarketvalues",
        {
          params,
        }
      );
      return response.data;
    } catch (err) {
      return handleError(err, "fetching country based market values failed");
    }
  };

  /**
   * Fetches company codes for the dropdown from the backend API.
   *
   * @param params - (Optional) Query parameters to filter company codes.
   * @returns {Promise<any>} - The response data containing company codes, or null if an error occurs.
   */
  const getCompanyCodes = async (params = {}) => {
    try {
      const response = await apiClient.get("/CascadingFilter/companycodes", {
        params,
      });
      return response.data;
    } catch (err) {
      return handleError(err, "fetching company codes failed");
    }
  };

  /**
   * Fetches reporting year values for the dropdown from the backend API.
   *
   * @param params - (Optional) Query parameters to filter reporting years.
   * @returns {Promise<any>} - The response data containing reporting years, or null if an error occurs.
   */
  const getReportingYear = async (params = {}) => {
    try {
      const response = await apiClient.get(
        "/CascadingFilter/reportingyearvalues",
        { params }
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Fetching reporting year values failed");
    }
  };

  /**
   * Fetches comparison year values for the dropdown from the backend API.
   *
   * @param params - (Optional) Query parameters to filter comparison years.
   * @returns {Promise<any>} - The response data containing comparison years, or null if an error occurs.
   */
  const getComparisonYear = async (params = {}) => {
    try {
      const response = await apiClient.get(
        "/CascadingFilter/comparisonyearvalues",
        { params }
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Fetching comparison year values failed");
    }
  };

  /**
   * Fetches reporting month values for the dropdown from the backend API.
   *
   * @param params - (Optional) Query parameters to filter reporting months.
   * @returns {Promise<any>} - The response data containing reporting months, or null if an error occurs.
   */
  const getReportingMonth = async (params = {}) => {
    try {
      const response = await apiClient.get(
        "/CascadingFilter/reportingmonthvalues",
        { params }
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Fetching reporting month values failed");
    }
  };

  /**
   * Fetches comparison month values for the dropdown from the backend API.
   *
   * @param params - (Optional) Query parameters to filter comparison months.
   * @returns {Promise<any>} - The response data containing comparison months, or null if an error occurs.
   */
  const getComparisonMonth = async (params = {}) => {
    try {
      const response = await apiClient.get(
        "/CascadingFilter/comparisonmonthvalues",
        { params }
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Fetching comparison month values failed");
    }
  };

  /**
   * Fetches all period details (reporting and comparison) for the dropdown from the backend API.
   *
   * @param params - (Optional) Query parameters to filter period details.
   * @returns {Promise<any>} - The response data containing period details, or null if an error occurs.
   */
  const getAllPeriodDetails = async (params = {}) => {
    try {
      const response = await apiClient.get("/CascadingFilter/PeriodDetails", {
        params,
      });
      return response.data;
    } catch (err) {
      return handleError(err, "Fetching Reporting/Comparison details failed");
    }
  };

  /**
   * Fetches review scenario values for the dropdown from the backend API.
   *
   * @param params - (Optional) Query parameters to filter review scenarios.
   * @returns {Promise<any>} - The response data containing review scenarios, or null if an error occurs.
   */
  const getReviewScenarioValues = async (params = {}) => {
    try {
      const response = await apiClient.get(
        "/CascadingFilter/reportingscenariovalues",
        { params }
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Fetching review scenario values failed");
    }
  };

  return {
    getMarketDropdownValues,
    getMcoDropdownValues,
    getCountryDropdownValues,
    getCompanyCodes,
    getReportingYear,
    getReportingMonth,
    getComparisonMonth,
    getComparisonYear,
    getReviewScenarioValues,
    countryBasedMarketValues,
    getAllPeriodDetails,
  };
};
